/**
 * Employer hiring requirement packs — certifications, credentialing, role specialties.
 *
 * Credential catalog rules (2026-10-09, recruiter feedback):
 *  - There is no generic "Ambulance Driver's License". Ambulance driving packs ask for EVOC
 *    (Emergency Vehicle Operator Course) as a certification, plus driver's license / driving record.
 *    California's real Ambulance Driver Certificate is a separate, CA-only credential.
 *  - The DOT medical card (medical examiner's certificate, item key 'mec') is a medical record: it is
 *    never part of a pack and never uploaded. A job that still lists 'mec' asks the candidate to
 *    confirm they hold it (jobRequiresMedicalCardAttestation).
 *  - Legacy keys stay readable: 'adl' displays as EVOC.
 *
 * Nothing medical before a job offer (2026-10-10, Part B1; EEOC "Preemployment Disability-Related
 * Questions and Medical Examinations": an employer "may not ask disability-related questions and may
 * not conduct medical examinations until after it makes a conditional job offer"):
 *  - MEDICAL_ITEM_KEYS (immunizations, TB, Hep B, physical, DOT medical card) are never required to
 *    apply, never counted in readiness or match scores, and only requested at Job Offer or Hired
 *    (Request documents or the hire checklist). Packs list them as afterOfferItems.
 * Employer-arranged items (Part B2): EMPLOYER_ARRANGED_ITEM_KEYS (background check, drug screen,
 * driving record, N95 fit test, bloodborne pathogens, HIPAA, physical agility) are shown to candidates
 * as "Your employer arranges this", never as uploads, gaps or apply blockers. Packs list them as
 * employerArrangedItems. An EMT may still upload a driving record (MVR) if they want.
 * Every place that reads a job's credentialingRequirements.requiredItems splits them with
 * splitCredentialingItems, so jobs saved before this change follow the same rule.
 */
export type RequirementPack = {
  id: string;
  label: string;
  category: 'role' | 'cert' | 'credential' | 'experience';
  roleType?: string;
  countyName?: string | null;
  requiredCertifications?: string[];
  preferredCertifications?: string[];
  requiredCredentialingItems?: string[];
  /** Medical items the employer requests after an offer (never to apply). */
  afterOfferItems?: string[];
  /** Items the employer arranges itself (shown to candidates as information only). */
  employerArrangedItems?: string[];
  requiredCourses?: string[];
  notes?: string | null;
  /**
   * 'driver' for driver packs (Ambulance Driver, Stretcher Van Driver): choosing the pack on Post a Job
   * sets the job's roleCategory to 'driver' while the `driver_roles` flag is on. 'dispatch' the same for
   * the Dispatcher pack. Absent = EMS role.
   */
  roleCategory?: 'driver' | 'dispatch';
  /** Offered on Post a Job only while this feature flag is on (the pack still resolves everywhere). */
  flag?: string;
};

export const REQUIREMENT_PACKS: RequirementPack[] = [
  { id: 'emt_b', label: 'EMT-B', category: 'role', roleType: 'EMT', requiredCertifications: ['NREMT-B', 'BLS'], requiredCredentialingItems: ['emt_license', 'bls'] },
  { id: 'aemt', label: 'AEMT', category: 'role', roleType: 'AEMT', requiredCertifications: ['NREMT-I', 'BLS'], requiredCredentialingItems: ['emt_license', 'bls'] },
  { id: 'paramedic', label: 'Paramedic', category: 'role', roleType: 'Paramedic', requiredCertifications: ['NREMT-P', 'BLS', 'ACLS'], preferredCertifications: ['PALS'], requiredCredentialingItems: ['emt_license', 'bls'] },
  { id: '911_emt', label: '911 EMT', category: 'role', roleType: 'EMT', requiredCertifications: ['NREMT-B', 'BLS'], preferredCertifications: ['EVOC'], requiredCredentialingItems: ['emt_license', 'bls', 'drivers_license'], afterOfferItems: ['immunizations'], employerArrangedItems: ['dmv_record'], requiredCourses: ['ics100', 'nims700'], notes: '911 response / emergency ambulance.' },
  { id: 'ift_emt', label: 'IFT EMT', category: 'role', roleType: 'EMT', requiredCertifications: ['NREMT-B', 'BLS'], preferredCertifications: ['EVOC'], requiredCredentialingItems: ['emt_license', 'bls', 'drivers_license'], notes: 'Interfacility transport.' },
  { id: 'event_emt', label: 'Event EMT', category: 'role', roleType: 'EMT', requiredCertifications: ['NREMT-B', 'BLS'], requiredCredentialingItems: ['emt_license', 'bls', 'drivers_license'], notes: 'Event / standby medicine.' },
  { id: 'fire_ems', label: 'Fire/EMS', category: 'role', roleType: 'EMT', requiredCertifications: ['NREMT-B', 'BLS'], requiredCredentialingItems: ['emt_license', 'bls', 'drivers_license'], notes: 'Fire-based EMS.' },
  { id: 'santa_clara_emt', label: 'Santa Clara EMT', category: 'role', roleType: 'EMT', countyName: 'Santa Clara', requiredCertifications: ['NREMT-B', 'BLS'], preferredCertifications: ['ACLS', 'California Ambulance Driver Certificate'], requiredCredentialingItems: ['emt_license', 'bls', 'drivers_license', 'county_badge'], afterOfferItems: ['immunizations'], employerArrangedItems: ['dmv_record'], requiredCourses: ['ics100', 'nims700', 'sems'] },
  { id: 'er_tech', label: 'ER Tech', category: 'role', roleType: 'EMT', requiredCertifications: ['BLS'], requiredCredentialingItems: ['bls'], afterOfferItems: ['immunizations'], notes: 'Emergency department technician.' },
  { id: 'ambulance_driver', label: 'Ambulance Driver', category: 'role', roleCategory: 'driver', requiredCertifications: ['EVOC'], requiredCredentialingItems: ['drivers_license'], employerArrangedItems: ['dmv_record'], notes: 'EVOC (Emergency Vehicle Operator Course) / ambulance driving.' },
  // Non-emergency stretcher / wheelchair van driving (2026-10-09, MedEx). No EMS certification and no
  // EVOC required; EVOC is preferred. Offered only while the driver_roles flag is on.
  { id: 'stretcher_van_driver', label: 'Stretcher Van Driver', category: 'role', roleCategory: 'driver', flag: 'driver_roles', preferredCertifications: ['EVOC'], requiredCredentialingItems: ['drivers_license'], employerArrangedItems: ['dmv_record'], notes: 'Non-emergency stretcher van driving: driver\'s license; the employer checks the driving record.' },
  // Dispatch work (2026-11-01): no certification is assumed; the employer lists any they want.
  { id: 'dispatcher', label: 'Dispatcher', category: 'role', roleCategory: 'dispatch', flag: 'driver_roles', notes: 'Dispatch center role. Matched on dispatch background and experience.' },
  { id: 'critical_care', label: 'Critical Care Transport', category: 'role', roleType: 'Paramedic', requiredCertifications: ['NREMT-P', 'ACLS'], preferredCertifications: ['PALS'], requiredCredentialingItems: ['emt_license', 'bls'] },
  { id: 'pediatric_exp', label: 'Pediatric experience', category: 'experience', preferredCertifications: ['PALS'], notes: 'Pediatric patient care experience.' },
  { id: 'cardiac_stemi', label: 'Cardiac/STEMI', category: 'experience', requiredCertifications: ['ACLS'], notes: 'Cardiac / STEMI program experience.' },
  { id: 'stroke', label: 'Stroke', category: 'experience', requiredCertifications: ['ACLS'], notes: 'Stroke center / stroke alert experience.' },
  { id: 'trauma', label: 'Trauma', category: 'experience', preferredCertifications: ['PHTLS', 'ITLS'], notes: 'Trauma system experience.' },
  { id: 'bls_cert', label: 'BLS', category: 'cert', requiredCertifications: ['BLS'], requiredCredentialingItems: ['bls'] },
  { id: 'als_cert', label: 'ALS', category: 'cert', requiredCertifications: ['ACLS'], preferredCertifications: ['NREMT-P'] },
  { id: 'nremt', label: 'Nationally Registered EMT', category: 'cert', requiredCertifications: ['NREMT-B'] },
  { id: 'state_license', label: 'State EMS Certification', category: 'credential', requiredCertifications: ['State EMS Certification'], requiredCredentialingItems: ['emt_license'] },
  { id: 'drivers_license', label: 'Driver license', category: 'credential', requiredCredentialingItems: ['drivers_license'] },
  { id: 'cpr_bls_card', label: 'CPR/BLS card', category: 'credential', requiredCredentialingItems: ['bls'] },
  { id: 'evoc', label: 'EVOC / ambulance driving', category: 'credential', requiredCertifications: ['EVOC'], requiredCredentialingItems: ['drivers_license'], employerArrangedItems: ['dmv_record'] },
  { id: 'clinical_hours', label: 'Clinical / ride hours', category: 'credential', notes: 'Clinical or ride-along hour documentation.' },
  { id: 'immunizations', label: 'Immunizations / medical clearance (after an offer)', category: 'credential', afterOfferItems: ['immunizations'], notes: 'Requested after a job offer, never to apply.' },
  { id: 'background_check', label: 'Background check', category: 'credential', employerArrangedItems: ['background_check'], notes: 'The employer arranges the background check.' },
  { id: 'drug_screen', label: 'Drug screen', category: 'credential', employerArrangedItems: ['drug_screen'], notes: 'The employer arranges the drug screen.' },
];

const PACK_MAP = new Map(REQUIREMENT_PACKS.map((p) => [p.id, p]));

export function getRequirementPackById(id: string): RequirementPack | undefined {
  return PACK_MAP.get(id);
}

/**
 * The packs a picker offers: a pack with a `flag` only while that flag is on. `keepId` keeps a pack a
 * job or checklist already uses visible, so an edit never silently drops it.
 */
export function offeredRequirementPacks(isFlagOn: (key: string) => boolean, keepId?: string | null): RequirementPack[] {
  return REQUIREMENT_PACKS.filter((p) => !p.flag || isFlagOn(p.flag) || p.id === keepId);
}

export const CREDENTIALING_ITEM_LABELS: Record<string, string> = {
  emt_license: 'EMT License',
  bls: 'AHA BLS/CPR',
  drivers_license: "Driver's License",
  // Medical: requested only after a job offer (B1). Never an apply-time item.
  mec: 'DOT medical card (after an offer)',
  // Legacy key only (no generic "Ambulance Driver's License" exists). Jobs and profiles that stored
  // 'adl' read it as EVOC; it is not offered for new jobs (CREDENTIALING_ITEM_PICKER_KEYS).
  adl: 'EVOC (Emergency Vehicle Operator Course)',
  dmv_record: 'DMV Driving Record',
  immunizations: 'Immunization Records',
  county_badge: 'County Accreditation / Badge',
  // Medical, after an offer only (B1).
  tb_test: 'TB test result',
  hep_b: 'Hepatitis B vaccination',
  physical_exam: 'Physical exam',
  // Arranged by the employer (B2).
  background_check: 'Background check',
  drug_screen: 'Drug screen',
  n95_fit_test: 'N95 fit test',
  bloodborne_pathogens: 'Bloodborne pathogens training',
  hipaa_training: 'HIPAA training',
  physical_agility: 'Physical agility test',
};

/** Medical items: only after a job offer (B1). */
export const MEDICAL_ITEM_KEYS: readonly string[] = ['immunizations', 'mec', 'tb_test', 'hep_b', 'physical_exam'];
/** Items the employer arranges (B2): information for the candidate, never homework. */
export const EMPLOYER_ARRANGED_ITEM_KEYS: readonly string[] = ['background_check', 'drug_screen', 'dmv_record', 'n95_fit_test', 'bloodborne_pathogens', 'hipaa_training', 'physical_agility'];

export const isMedicalItem = (key: unknown): boolean => typeof key === 'string' && MEDICAL_ITEM_KEYS.includes(key);
export const isEmployerArrangedItem = (key: unknown): boolean => typeof key === 'string' && EMPLOYER_ARRANGED_ITEM_KEYS.includes(key);

/** A job's or checklist's items: what a candidate brings to apply, what comes after an offer, what the employer arranges. */
export function splitCredentialingItems(items: unknown): { beforeApply: string[]; afterOffer: string[]; employerArranges: string[] } {
  const list = Array.isArray(items) ? items.filter((x): x is string => typeof x === 'string' && x.trim().length > 0) : [];
  return {
    beforeApply: list.filter((k) => !isMedicalItem(k) && !isEmployerArrangedItem(k)),
    afterOffer: list.filter(isMedicalItem),
    employerArranges: list.filter(isEmployerArrangedItem),
  };
}

/** Every item a pack puts on a job: what to bring, then after-offer, then employer-arranged. */
export function packCredentialingItems(pack: Pick<RequirementPack, 'requiredCredentialingItems' | 'afterOfferItems' | 'employerArrangedItems'>): string[] {
  return [...new Set([...(pack.requiredCredentialingItems ?? []), ...(pack.afterOfferItems ?? []), ...(pack.employerArrangedItems ?? [])])];
}

/** Labels a key was stored under before a relabel (credentialing summaries keep the old text). */
const PREVIOUS_CREDENTIALING_ITEM_LABELS: Record<string, string[]> = {
  adl: ["Ambulance Driver's License"],
  mec: ['Medical Examiner Certificate'],
};

/** Every label an item key may appear under in stored data: the current one first, then older ones. */
export function credentialingItemLabelsForMatch(key: string): string[] {
  const current = CREDENTIALING_ITEM_LABELS[key];
  return [...(current ? [current] : []), ...(PREVIOUS_CREDENTIALING_ITEM_LABELS[key] ?? [])];
}

/** Credentialing item keys kept only so stored data still reads; never offered for new use. */
export const LEGACY_CREDENTIALING_ITEM_KEYS: readonly string[] = ['adl'];

/**
 * Credentialing items an employer can pick for a job. Legacy keys are left out; a picker shows one
 * only while a job being edited already has it selected, so it can be removed.
 */
export const CREDENTIALING_ITEM_PICKER_KEYS: string[] = Object.keys(CREDENTIALING_ITEM_LABELS).filter(
  (key) => !LEGACY_CREDENTIALING_ITEM_KEYS.includes(key),
);

/**
 * True when the job lists the DOT medical card (credentialing item 'mec'). The candidate confirms
 * they hold it when applying (it is a medical record, never uploaded). Safe on any job shape.
 */
/**
 * Retired 2026-10-10 (B1): the apply-time "I have a current DOT medical card" attestation is gone. A
 * medical card is never asked for before a job offer. Kept so older callers compile; always false.
 */
export function jobRequiresMedicalCardAttestation(_job: unknown): boolean {
  return false;
}

export const REQUIRED_COURSE_LABELS: Record<string, string> = {
  ics100: 'ICS-100',
  nims700: 'NIMS 700',
  sems: 'SEMS',
  awr160: 'AWR-160',
  fro: 'FRO',
};

/** Optional add-on credential toggles (not tied to a single pack). */
export const EXTRA_CREDENTIALING_TOGGLES: { key: string; label: string }[] = [
  // Background check and drug screen are arranged by the employer (B2): listed for the candidate's
  // information, never scored or asked of them.
  { key: 'background_check', label: 'Background check (you arrange it)' },
  { key: 'drug_screen', label: 'Drug screen (you arrange it)' },
  { key: 'county_badge', label: 'County badge / accreditation' },
];
